import { QimaSnapChecklistGuard } from '@aca-new/app/pages/checklists/shared/guards/qima-snap-checklist.guard';
import lifeScienceGuard from '@aca-new/app/pages/life-science/shared/guards/life-science.guard';
import { MainComponent } from '@aca-new/app/pages/main/main.component';
import { HtmlReportGuard } from '@aca-new/app/pages/reports/pages/interactive-report/shared/guards/html-report.guard';
import { InteractiveReportGuard } from '@aca-new/app/pages/reports/pages/interactive-report/shared/guards/interactive-report.guard';
import { PageNotFoundComponent } from '@aca-new/app/shared/components/page-not-found/page-not-found.component';
import { appcuesLoadGuard$ } from '@aca-new/app/shared/guards/appcues-load/appcues-load.guard';
import { AuthoritiesGuard } from '@aca-new/app/shared/guards/authorities/authorities.guard';
import businessUnitGuard from '@aca-new/app/shared/guards/business-unit/business-unit.guard';
import customSettingGuard from '@aca-new/app/shared/guards/custom-setting/custom-setting.guard';
import { DomainGuard } from '@aca-new/app/shared/guards/domain/domain.guard';
import { EntryGuard } from '@aca-new/app/shared/guards/entry/entry.guard';
import { logoutFromAcaGuard } from '@aca-new/app/shared/guards/logout-from-aca/logout-from-aca.guard';
import { RestoreUserDataGuard } from '@aca-new/app/shared/guards/restore-user-data/restore-user-data.guard';
import { SwUpdateCheckGuard } from '@aca-new/app/shared/guards/sw-update-check/sw-update-check.guard';
import { TitleFaviconResolver } from '@aca-new/app/shared/resolvers/title-favicon-resolver.ts/title-favicon-resolver.service';
import { inject, NgModule, Type } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterModule, Routes } from '@angular/router';

const ROUTES: Routes = [
  {
    path: 'interactive-report-v2/:orderId/:productId',
    canActivate: [DomainGuard, AuthoritiesGuard, InteractiveReportGuard],
    title: TitleFaviconResolver,
    data: {
      altTitle: 'Reports | Detailed (v2)',
      routeKey: 'interactive-report-v2',
    },
    loadChildren: (): Promise<Type<unknown>> =>
      import('@aca-new/app/pages/reports/pages/interactive-report/interactive-report.module').then((m): Type<unknown> => m.InteractiveReportModule),
  },
  {
    path: 'interactive-report-v2-1/:orderId/:productId',
    canActivate: [DomainGuard, AuthoritiesGuard, InteractiveReportGuard, appcuesLoadGuard$],
    title: TitleFaviconResolver,
    data: {
      altTitle: 'Reports | Detailed (v2.1)',
      routeKey: 'interactive-report-v2-1',
    },
    loadChildren: (): Promise<Type<unknown>> =>
      import('@aca-new/app/pages/reports/pages/interactive-report/interactive-report.module').then((m): Type<unknown> => m.InteractiveReportModule),
  },
  // Site layout routes goes here
  {
    path: '',
    canActivate: [DomainGuard, AuthoritiesGuard, EntryGuard, appcuesLoadGuard$],
    canActivateChild: [businessUnitGuard, customSettingGuard],
    component: MainComponent,
    children: [
      {
        path: '',
        redirectTo: 'home',
        pathMatch: 'full',
      },
      {
        path: 'home',
        title: TitleFaviconResolver,
        data: {
          altTitle: 'Homepage',
        },
        loadChildren: (): Promise<Type<unknown>> => import('./pages/home/home.module').then((m): Type<unknown> => m.HomeModule),
      },
      {
        path: 'analytics',
        title: TitleFaviconResolver,
        data: {
          altTitle: 'Analytics',
        },
        loadChildren: (): Promise<Type<unknown>> => import('./pages/analytics/analytics.module').then((m): Type<unknown> => m.AnalyticsModule),
      },
      {
        path: 'non-conformances',
        title: TitleFaviconResolver,
        data: {
          altTitle: 'Non-conformances',
        },
        loadChildren: (): Promise<Type<unknown>> => import('./pages/non-conformances/non-conformances.module').then((m): Type<unknown> => m.NonConformancesModule),
      },
      {
        path: 'bookings',
        title: TitleFaviconResolver,
        data: {
          altTitle: 'Bookings',
        },
        loadChildren: (): Promise<Type<unknown>> => import('./pages/bookings/bookings.module').then((m): Type<unknown> => m.BookingsModule),
      },
      {
        path: 'book-inspection',
        title: TitleFaviconResolver,
        data: {
          altTitle: 'Book Inspection',
          routeKey: 'book-inspection',
        },
        loadChildren: (): Promise<Type<unknown>> =>
          import('./pages/book/pages/product-inspections/product-inspections.module').then((m): Type<unknown> => m.ProductInspectionsModule),
      },
      {
        path: 'book-food-certification',
        loadChildren: (): Promise<Type<unknown>> => import('./pages/book/pages/food-certification/food-certification.module').then((m): Type<unknown> => m.FoodCertificationModule),
      },
      {
        path: 'book-checkout/:draftId',
        title: TitleFaviconResolver,
        data: {
          altTitle: 'Book Checkout',
        },
        loadChildren: (): Promise<Type<unknown>> => import('./pages/book/pages/checkout/checkout.module').then((m): Type<unknown> => m.CheckoutModule),
      },
      {
        path: 'book-confirmation/:serviceType/:orderId',
        title: TitleFaviconResolver,
        data: {
          altTitle: 'Book Confirmation',
        },
        loadChildren: (): Promise<Type<unknown>> => import('./pages/book/pages/confirmation/confirmation.module').then((m): Type<unknown> => m.ConfirmationModule),
      },
      {
        path: 'reports',
        title: TitleFaviconResolver,
        data: {
          altTitle: 'Reports',
          key: 'reports',
        },
        loadChildren: (): Promise<Type<unknown>> => import('./pages/reports/reports.module').then((m): Type<unknown> => m.ReportsModule),
      },
      {
        path: 'cap',
        title: TitleFaviconResolver,
        data: {
          altTitle: 'Corrective Action Plan',
        },
        loadChildren: (): Promise<Type<unknown>> =>
          import('./pages/reports/shared/components/corrective-action-plans/corrective-action-plans.module').then((m): Type<unknown> => m.CorrectiveActionPlansModule),
      },
      {
        path: 'broker',
        title: TitleFaviconResolver,
        data: {
          altTitle: 'Broker',
        },
        loadChildren: (): Promise<Type<unknown>> => import('./pages/reports/reports.module').then((m): Type<unknown> => m.ReportsModule),
      },
      {
        path: 'produce-reports',
        title: TitleFaviconResolver,
        data: {
          altTitle: 'Produce Reports',
        },
        loadChildren: (): Promise<Type<unknown>> => import('./pages/produce-reports/produce-reports.module').then((m): Type<unknown> => m.ProduceReportsModule),
      },
      {
        path: 'campaign',
        children: [
          {
            path: 'proceed',
            title: TitleFaviconResolver,
            data: {
              altTitle: 'Campaign',
              altFavicon: 'favicon2.ico',
            },
            canActivate: [AuthoritiesGuard],
            loadChildren: (): Promise<Type<unknown>> =>
              import('./pages/campaign/pages/campaign-proceed/campaign-proceed.module').then((m): Type<unknown> => m.CampaignProceedModule),
          },
        ],
      },

      {
        path: 'fields',
        title: TitleFaviconResolver,
        data: {
          altTitle: 'Site/Producer',
        },
        loadComponent: (): Promise<Type<unknown>> => import('./pages/units/field/field.component').then((m): Type<unknown> => m.FieldComponent),
      },
      {
        path: 'field',
        loadChildren: (): Promise<Type<unknown>> => import('./pages/units/field/field.module').then((m): Type<unknown> => m.FieldModule),
      },
      {
        path: 'facilities',
        title: TitleFaviconResolver,
        data: {
          altTitle: 'Facilities',
        },
        loadComponent: (): Promise<Type<unknown>> => import('./pages/units/facility/facility.component').then((m): Type<unknown> => m.FacilityComponent),
      },
      {
        path: 'facility',
        loadChildren: (): Promise<Type<unknown>> => import('./pages/units/facility/facility.module').then((m): Type<unknown> => m.FacilityModule),
      },
      {
        path: 'producers',
        title: TitleFaviconResolver,
        data: {
          altTitle: 'Legal Entity',
        },
        loadComponent: (): Promise<Type<unknown>> => import('./pages/units/producer/producer.component').then((m): Type<unknown> => m.ProducerComponent),
      },
      {
        path: 'producer',
        loadChildren: (): Promise<Type<unknown>> => import('./pages/units/producer/producer.module').then((m): Type<unknown> => m.ProducerModule),
      },
      {
        path: 'shippers',
        title: TitleFaviconResolver,
        data: {
          altTitle: 'Shippers',
        },
        loadComponent: (): Promise<Type<unknown>> => import('./pages/units/shipper/shipper.component').then((m): Type<unknown> => m.ShipperComponent),
      },
      {
        path: 'shipper',
        loadChildren: (): Promise<Type<unknown>> => import('./pages/units/shipper/shipper.module').then((m): Type<unknown> => m.ShipperModule),
      },
      {
        path: 'life-sciences',
        title: TitleFaviconResolver,
        canActivate: [lifeScienceGuard],
        data: {
          altTitle: 'Life Sciences',
        },
        loadChildren: (): Promise<Routes> => import('./pages/life-science/life-science.routes').then((m): Routes => m.LIFE_SCIENCE_ROUTES),
      },
      {
        path: 'supplier-documents',
        title: TitleFaviconResolver,
        data: {
          altTitle: 'Supplier Documents',
        },
        loadChildren: (): Promise<Type<unknown>> => import('./pages/supplier-documents/supplier-documents.module').then((m): Type<unknown> => m.SupplierDocumentsModule),
      },
      {
        path: 'checklists',
        title: TitleFaviconResolver,
        data: {
          altTitle: 'Checklists',
        },
        canActivate: [QimaSnapChecklistGuard],
        loadChildren: (): Promise<Type<unknown>> => import('./pages/checklists/checklists.module').then((m): Type<unknown> => m.ChecklistsModule),
      },
      {
        path: '',
        title: TitleFaviconResolver,
        data: {
          altTitle: 'Suppliers & Sites',
        },
        loadChildren: (): Promise<Type<unknown>> => import('./pages/suppliers/suppliers.module').then((m): Type<unknown> => m.SuppliersModule),
      },
      {
        path: 'payments',
        title: TitleFaviconResolver,
        data: {
          altTitle: 'Payments',
        },
        loadChildren: (): Promise<Type<unknown>> => import('./pages/payments/payments.module').then((m): Type<unknown> => m.PaymentsModule),
      },
      {
        path: 'account',
        loadChildren: (): Promise<Type<unknown>> => import('./pages/account/account.module').then((m): Type<unknown> => m.AccountModule),
      },
      {
        path: 'network',
        loadChildren: (): Promise<Type<unknown>> => import('./pages/network/network.module').then((m): Type<unknown> => m.NetworkModule),
      },
      {
        path: 'aca',
        data: {
          routeKey: 'aca-frame-wrapper',
        },
        loadChildren: (): Promise<Type<unknown>> =>
          import('./pages/main/shared/components/aca-frame-wrapper/aca-frame-wrapper.module').then((m): Type<unknown> => m.AcaFrameWrapperModule),
      },
      {
        path: 'interactive-report/:orderId/:productId',
        title: TitleFaviconResolver,
        data: {
          altTitle: 'Reports | Detailed',
          routeKey: 'interactive-report',
        },
        loadChildren: (): Promise<Type<unknown>> =>
          import('@aca-new/app/pages/reports/pages/interactive-report/interactive-report.module').then((m): Type<unknown> => m.InteractiveReportModule),
      },
      {
        path: 'reflow-report/:orderId/:orderNumber',
        title: TitleFaviconResolver,
        data: {
          altTitle: 'Reflow',
        },
        loadChildren: (): Promise<Type<unknown>> => import('./pages/reflow/reflow.module').then((m): Type<unknown> => m.ReflowModule),
      },
    ],
  },

  // No layout routes goes here
  {
    path: 'html-report/:orderId/:productId/:mode',
    canActivate: [DomainGuard, AuthoritiesGuard, HtmlReportGuard],
    loadChildren: (): Promise<Type<unknown>> =>
      import('@aca-new/app/pages/reports/pages/interactive-report/interactive-report.module').then((m): Type<unknown> => m.InteractiveReportModule),
  },
  {
    path: 'reflow/:orderId/:orderNumber',
    canActivate: [DomainGuard, AuthoritiesGuard],
    loadChildren: (): Promise<Type<unknown>> => import('./pages/reflow/reflow.module').then((m): Type<unknown> => m.ReflowModule),
  },
  {
    path: 'image-viewer',
    title: TitleFaviconResolver,
    data: {
      altTitle: 'Image Viewer',
      altFavicon: 'favicon2.ico',
    },
    loadChildren: (): Promise<Type<unknown>> => import('./pages/image-viewer/image-viewer.module').then((m): Type<unknown> => m.ImageViewerModule),
  },
  {
    path: 'supplier-confirmation-hub/:orderId',
    title: TitleFaviconResolver,
    data: {
      altTitle: 'Supplier Confirmation Hub',
      altFavicon: 'favicon2.ico',
    },
    loadChildren: (): Promise<Type<unknown>> =>
      import('./pages/supplier-confirmation-hub/supplier-confirmation-hub.module').then((m): Type<unknown> => m.SupplierConfirmationHubModule),
  },
  {
    path: 'supplier-confirmation/:type/:orderId',
    title: TitleFaviconResolver,
    data: {
      altTitle: 'Supplier Confirmation',
      altFavicon: 'favicon2.ico',
    },
    loadChildren: (): Promise<Type<unknown>> => import('./pages/supplier-confirmation/supplier-confirmation.module').then((m): Type<unknown> => m.SupplierConfirmationModule),
  },
  {
    path: 'supplier-confirmation/:orderId',
    title: TitleFaviconResolver,
    data: {
      altTitle: 'Supplier Confirmation',
      altFavicon: 'favicon2.ico',
    },
    loadChildren: (): Promise<Type<unknown>> => import('./pages/supplier-confirmation/supplier-confirmation.module').then((m): Type<unknown> => m.SupplierConfirmationModule),
  },
  {
    path: 'content-view/supplier-confirmation/:type/:orderId',
    redirectTo: 'supplier-confirmation/:type/:orderId',
  },
  {
    path: 'content-view/supplier-confirmation/:orderId',
    redirectTo: 'supplier-confirmation/:orderId',
  },
  {
    path: 'content-view',
    title: TitleFaviconResolver,
    data: {
      altTitle: 'Content View',
    },
    loadChildren: (): Promise<Type<unknown>> => import('@aca-new/app/pages/content-view/content-view.module').then((m): Type<unknown> => m.ContentViewModule),
  },
  {
    path: 'cap-receiver',
    title: TitleFaviconResolver,
    data: {
      altTitle: 'Corrective Action Plan',
    },
    loadChildren: (): Promise<Type<unknown>> =>
      import('./pages/reports/shared/components/corrective-action-plans/corrective-action-plans-receiver.module').then((m): Type<unknown> => m.CorrectiveActionPlansReceiverModule),
  },
  // campaign/reject and campaign/reject/result are public; campaign/proceed is under main layout (see main route children)
  {
    path: 'campaign',
    title: TitleFaviconResolver,
    data: {
      altTitle: 'Campaign',
      altFavicon: 'favicon2.ico',
    },
    loadChildren: (): Promise<Type<unknown>> => import('./pages/campaign/campaign.module').then((m): Type<unknown> => m.CampaignModule),
  },
  {
    path: 'pay',
    title: TitleFaviconResolver,
    canActivate: [appcuesLoadGuard$],
    data: {
      altTitle: 'Payment Portal',
      altFavicon: 'favicon2.ico',
    },
    loadChildren: (): Promise<Type<unknown>> => import('./pages/payment-link/payment-link.module').then((m): Type<unknown> => m.PaymentLinkModule),
  },
  {
    path: 'pay-pre-filled',
    title: TitleFaviconResolver,
    canActivate: [appcuesLoadGuard$],
    data: {
      altTitle: 'Payment Portal',
      altFavicon: 'favicon2.ico',
    },
    loadChildren: (): Promise<Type<unknown>> => import('./pages/payment-link-prefill/payment-link-prefill.module').then((m): Type<unknown> => m.PaymentLinkPrefillModule),
  },
  {
    path: 'payments-for-aca',
    title: TitleFaviconResolver,
    canActivate: [AuthoritiesGuard],
    data: {
      altTitle: 'Payments',
    },
    loadChildren: (): Promise<Type<unknown>> => import('./pages/payments/payments.module').then((m): Type<unknown> => m.PaymentsModule),
  },
  {
    path: 'payment-checkout-aca/:orderId',
    canActivate: [AuthoritiesGuard],
    title: TitleFaviconResolver,
    data: {
      altTitle: 'Payment Checkout',
    },
    loadComponent: (): Promise<Type<unknown>> => import('./pages/payment-checkout/payment-checkout.component').then((m): Type<unknown> => m.PaymentCheckoutComponent),
  },
  {
    path: 'shared-report/:token',
    title: TitleFaviconResolver,
    // RestoreUserDataGuard: If user is already logged in another tab, restore user data to enable auto-login
    // This implements the new login flow requirement from SP-26364
    canActivate: [RestoreUserDataGuard],
    data: {
      altTitle: 'Interactive Report',
      altFavicon: 'favicon2.ico',
    },
    loadChildren: (): Promise<Type<unknown>> =>
      import('./pages/reports/pages/external-shared-report/external-shared-report.module').then((m): Type<unknown> => m.ExternalSharedReportModule),
  },
  {
    path: 'v2/login',
    title: TitleFaviconResolver,
    canActivate: [DomainGuard, RestoreUserDataGuard],
    data: {
      altTitle: 'Login',
      enableEmailOtpEntry: true,
    },
    loadComponent: (): Promise<Type<unknown>> => import('./pages/login/login.component').then((m): Type<unknown> => m.LoginComponent),
  },
  {
    path: 'login/user/:userId/:userName/token/:token/:cacheType',
    canActivate: [
      (route: ActivatedRouteSnapshot): boolean => {
        const router = inject(Router);

        void router.navigate(['/v2/login'], {
          queryParams: {
            fromBackOffice: true,
            userId: route.params.userId,
            userName: route.params.userName,
            token: route.params.token,
            cacheType: route.params.cacheType,
          },
          replaceUrl: true,
        });

        return false;
      },
    ],
    children: [],
  },
  {
    path: 'login',
    title: TitleFaviconResolver,
    canActivate: [DomainGuard],
    loadChildren: (): Promise<Type<unknown>> => import('./pages/user/pages/login/login.module').then((m): Type<unknown> => m.LoginModule),
  },
  {
    path: 'forgot-credentials',
    title: TitleFaviconResolver,
    canActivate: [DomainGuard],
    data: {
      altTitle: 'Forgot Credentials',
    },
    loadComponent: (): Promise<Type<unknown>> =>
      import('./pages/login/pages/forgot-credentials/forgot-credentials.component').then((m): Type<unknown> => m.ForgotCredentialsComponent),
  },
  {
    path: 'reset',
    title: TitleFaviconResolver,
    canActivate: [DomainGuard],
    data: {
      altTitle: 'Reset Password',
    },
    loadComponent: (): Promise<Type<unknown>> => import('./pages/login/pages/reset-password/reset-password.component').then((m): Type<unknown> => m.ResetPasswordComponent),
  },
  {
    path: 'multi-factor-auth',
    loadChildren: (): Promise<Routes> => import('./pages/user/pages/multi-factor-auth/multi-factor-auth.routes').then((m): Routes => m.ROUTES),
  },
  {
    path: 'document-verification',
    title: TitleFaviconResolver,
    canActivate: [DomainGuard],
    loadChildren: (): Promise<Type<unknown>> => import('./pages/document-verification/document-verification.module').then((m): Type<unknown> => m.DocumentVerificationModule),
  },
  {
    path: '404',
    title: TitleFaviconResolver,
    data: {
      altTitle: '404',
      altFavicon: 'favicon2.ico',
    },
    component: PageNotFoundComponent,
  },
  {
    path: 'entity/:entityId/accept-invite',
    loadChildren: (): Promise<Type<unknown>> =>
      import('./pages/network/pages/create-supplier-account/supplier-account-external.module').then((m): Type<unknown> => m.SupplierAccountExternalModule),
  },
  {
    path: 'qima-wechat',
    title: TitleFaviconResolver,
    loadChildren: (): Promise<Routes> => import('./pages/wechat/wechat.routes').then((m): Routes => m.WECHAT_ROUTES),
  },
  {
    path: 'logout-from-aca',
    canActivate: [logoutFromAcaGuard],
    children: [],
  },
  // otherwise, check for SW update before redirecting to home page
  {
    path: '**',
    canActivate: [SwUpdateCheckGuard],
    children: [],
  },
];

@NgModule({
  imports: [RouterModule.forRoot(ROUTES, { onSameUrlNavigation: 'reload' })],
  exports: [RouterModule],
  providers: [DomainGuard, EntryGuard],
})
export class AppRoutingModule {}
